import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import { constructMessage } from "../../../common/NotificationAdmin/CreateNotification";
import { notificationsApi, notificationRead } from "../../../services/NotificationServices";
import { DispatchContext } from "../../../store";
import { Images } from "../../../common/Assets/Images";
import PageWrapper from "../../../common/PageWrapper/PageWrapper";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import NotificationsIcon from "@mui/icons-material/Notifications";

export default function Notifications() {
  const dispatch = useContext(DispatchContext);
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [markingAsRead, setMarkingAsRead] = useState(null);
  const containerRef = useRef();
  const isNotificationsPage = location.pathname === '/management/notifications';

  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.id || null;
  };

  useEffect(() => {
    if (isNotificationsPage) {
      fetchNotificationList();
    }
  }, [page, pageSize, isNotificationsPage]);

  const fetchNotificationList = () => {
    const userId = getCurrentUserId();
    if (!userId) {
      console.error('No user ID found');
      return;
    }

    // Don't set loading state for background refreshes
    const isBackgroundRefresh = !loading;

    if (isBackgroundRefresh) {
      // Silent background refresh
      notificationsApi("get", userId, null, {
        page: page,
        per_page: pageSize,
      }).then((res) => {
        setNotifications(res?.data || []);
        setMeta(res?.meta || {});
      }).catch((error) => {
        console.error('Error fetching notifications:', error);
      });
    } else {
      // Initial load with loading state
      dispatch({ type: 'IS_NOT_LOADING', payload: false });
      notificationsApi("get", userId, null, {
        page: page,
        per_page: pageSize,
      }).then((res) => {
        setLoading(false);
        setNotifications(res?.data || []);
        setMeta(res?.meta || {});
        dispatch({ type: 'IS_NOT_LOADING', payload: false });
      }).catch((error) => {
        setLoading(false);
        dispatch({ type: 'IS_NOT_LOADING', payload: false });
        console.error('Error fetching notifications:', error);
      });
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  const markAsRead = (notificationId) => {
    setMarkingAsRead(notificationId);
    notificationRead("get", notificationId).then(() => {
      fetchNotificationList();
      // Update sidebar notification count
      updateSidebarNotificationCount();
      setMarkingAsRead(null);
    }).catch((error) => {
      console.error('Error marking notification as read:', error);
      setMarkingAsRead(null);
    });
  };

  const updateSidebarNotificationCount = () => {
    // This will be called to update the sidebar count
    // The sidebar will automatically update when pathname changes
    const event = new CustomEvent('notificationCountUpdate');
    window.dispatchEvent(event);
  };

  const formatNotificationMessage = (notification) => {
    return constructMessage(notification);
  };

  return (
    <PageWrapper>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ background: "#ffffff", p: 2, mb: 2, display: "flex", flexDirection: "row", gap: 1, borderRadius: 1 }}>
          <NotificationsIcon sx={{ fontSize: 32, color: '#ff9653', mb: 1, mt:2 }} />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, mt:2, color: 'primary.main' }}>
            Notifications
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '60px' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications?.length > 0 ? (
                notifications.map((notification, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: notification?.unread === true ? '#f8f9fa' : 'white',
                      '&:hover': { bgcolor: '#f0f0f0' }
                    }}
                  >
                    <TableCell sx={{ width: '60px' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#666' }}>
                        {(page - 1) * pageSize + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          maxWidth: 400
                        }}
                      >
                        {formatNotificationMessage(notification)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="primary" fontWeight={600}>
                        {notification?.naturaltime}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {notification?.unread && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#1976d2',
                            cursor: markingAsRead === notification?.id ? 'not-allowed' : 'pointer',
                            textDecoration: 'underline',
                            '&:hover': markingAsRead === notification?.id ? {} : { color: '#1565c0' },
                            opacity: markingAsRead === notification?.id ? 0.6 : 1,
                          }}
                          onClick={() => markingAsRead !== notification?.id && markAsRead(notification?.id)}
                        >
                          {markingAsRead === notification?.id ? 'Marking as read...' : 'Mark as read'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      ------ No Notification found! ------
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {meta?.total > pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 4, mb: 2 }}>
            {/* Rows per page dropdown and range info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Rows per page:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  sx={{
                    height: 32,
                    '& .MuiOutlinedInput-input': {
                      padding: '6px 8px',
                      fontSize: '14px',
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              {/* Range display with navigation arrows */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Previous Arrow */}
                <Box
                  onClick={() => page > 1 && handlePageChange(null, page - 1)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: page > 1 ? '#5B6B8F' : '#e0e0e0',
                    color: page > 1 ? 'white' : '#999',
                    cursor: page > 1 ? 'pointer' : 'not-allowed',
                    fontSize: '12px',
                    '&:hover': page > 1 ? { backgroundColor: '#4A5A7F' } : {},
                  }}
                >
                  ‹
                </Box>

                <Typography variant="body2" sx={{ color: '#666' }}>
                  {`${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, meta?.total)} of ${meta?.total}`}
                </Typography>

                {/* Next Arrow */}
                <Box
                  onClick={() => page < Math.ceil(meta?.total / pageSize) && handlePageChange(null, page + 1)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: page < Math.ceil(meta?.total / pageSize) ? '#5B6B8F' : '#e0e0e0',
                    color: page < Math.ceil(meta?.total / pageSize) ? 'white' : '#999',
                    cursor: page < Math.ceil(meta?.total / pageSize) ? 'pointer' : 'not-allowed',
                    fontSize: '12px',
                    '&:hover': page < Math.ceil(meta?.total / pageSize) ? { backgroundColor: '#4A5A7F' } : {},
                  }}
                >
                  ›
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </PageWrapper>
  );
}
